import { morphSection } from '@theme/section-renderer';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Shipping Protection toggle, panier.
 * 2026-09-07 : remplace l'ancien script inline qui terminait par
 * window.location.reload() -- ce qui fermait le tiroir sur mobile et
 * quittait la page /cart. Le reste du panier (component-cart-items.js,
 * cart-discount.js) suit toujours le meme schema : POST vers /cart/add.js
 * ou /cart/change.js avec sections=[sectionId] dans le body, puis
 * morphSection() sur le HTML de section renvoye, sans jamais recharger.
 *
 * IMPORTANT : /cart/add.js et /cart/change.js n'ont pas le meme format de
 * reponse. /cart/change.js renvoie l'objet panier complet (token, currency,
 * total_price...), /cart/add.js renvoie seulement { items: [...] } sans ces
 * champs. CartLinesUpdateEvent.createCartFromAjaxResponse() suppose le
 * format complet et plante (Intl.NumberFormat sans currency) si on le lui
 * passe une reponse add.js. Aucun consommateur reel de l'evenement (cart-
 * icon.js, cart-drawer.js, component-cart-items.js) ne lit le champ cart en
 * pratique, ils utilisent tous detail -- donc cart: null cote add est un
 * choix sur et delibere, pas un oubli.
 */
class CozoraShippingProtection extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('.cozora-sp__input').forEach((el) => this.#bind(el));
    this.#autoSync();
  }

  get #sectionId() {
    return this.dataset.sectionId || '';
  }

  get #isDrawer() {
    return Boolean(this.closest('theme-drawer'));
  }

  #setDisabled(disabled) {
    this.querySelectorAll('.cozora-sp__input').forEach((o) => {
      o.disabled = disabled;
    });
  }

  #bind(el) {
    if (el.dataset.bound) return;
    el.dataset.bound = '1';
    el.addEventListener('change', () => this.#onChange(el));
  }

  /**
   * @param {HTMLInputElement} input
   */
  #onChange(input) {
    const OPTOUT = 'cozora_sp_optout';
    this.#setDisabled(true);
    sessionStorage.setItem(OPTOUT, input.checked ? '0' : '1');

    const sectionId = this.#sectionId;
    const isAdd = input.checked;
    const url = window.Shopify.routes.root + (isAdd ? 'cart/add.js' : 'cart/change.js');
    const body = isAdd
      ? { items: [{ id: Number(input.dataset.variantId), quantity: 1 }], sections: sectionId }
      : { id: input.dataset.lineKey, quantity: 0, sections: sectionId };

    const deferredPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: isAdd ? 'add' : 'remove',
        context: 'cart',
        lines: [
          isAdd
            ? { merchandiseId: Number(input.dataset.variantId), quantity: 1 }
            : { id: input.dataset.lineKey, quantity: 0 },
        ],
        promise: deferredPromise.promise,
      })
    );

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (!response.ok) throw new Error('cart ' + response.status);
        return response.json();
      })
      .then((data) => {
        deferredPromise.resolve({
          // /cart/add.js response ({items:[...]}) is not a full cart object
          // (no token/currency/total_price) -- createCartFromAjaxResponse
          // would throw. Only /cart/change.js returns the full shape.
          cart: isAdd ? null : CartLinesUpdateEvent.createCartFromAjaxResponse(data),
          detail: {
            sections: data.sections,
            items: data.items,
            itemCount: data.item_count,
            source: 'cozora-shipping-protection',
            didError: false,
          },
        });

        const html = data.sections && data.sections[sectionId];
        if (html) {
          morphSection(sectionId, html, { mode: this.#isDrawer ? 'hydration' : 'full' });
        } else {
          window.location.reload();
        }
      })
      .catch((error) => {
        input.checked = !input.checked;
        deferredPromise.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      })
      .finally(() => this.#setDisabled(false));
  }

  /**
   * Auto-add on first cart view, auto-clamp to quantity 1, same rules as
   * before, but going through morphSection instead of a full reload.
   */
  #autoSync() {
    if (window.__cozoraSpSynced) return;

    const all = this.querySelectorAll('.cozora-sp__input');
    const el = all[0];
    if (!el) return;

    const qty = Number(el.dataset.qty || 0);
    const OPTOUT = 'cozora_sp_optout';
    const sectionId = this.#sectionId;

    if (qty > 1) {
      window.__cozoraSpSynced = true;
      this.#setDisabled(true);
      fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: el.dataset.lineKey, quantity: 1, sections: sectionId }),
      })
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error('cart ' + response.status))))
        .then((data) => {
          const html = data.sections && data.sections[sectionId];
          if (html) morphSection(sectionId, html, { mode: this.#isDrawer ? 'hydration' : 'full' });
        })
        .catch(() => {})
        .finally(() => this.#setDisabled(false));
      return;
    }

    if (qty === 0 && sessionStorage.getItem(OPTOUT) !== '1') {
      window.__cozoraSpSynced = true;
      this.#setDisabled(true);
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(el.dataset.variantId), quantity: 1 }],
          sections: sectionId,
        }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('cart ' + response.status);
          return response.json();
        })
        .then((data) => {
          const html = data.sections && data.sections[sectionId];
          if (html) morphSection(sectionId, html, { mode: this.#isDrawer ? 'hydration' : 'full' });
        })
        .catch(() => {
          sessionStorage.setItem(OPTOUT, '1');
        })
        .finally(() => this.#setDisabled(false));
    }
  }
}

if (!customElements.get('cozora-shipping-protection')) {
  customElements.define('cozora-shipping-protection', CozoraShippingProtection);
}
